import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { StateBadge } from '@/components/platform/StateBadge';
import { useInstitutionTypeLabel } from '@/components/platform/useLabel';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import type {
  AttentionItem,
  InstitutionTypeCount,
  IntegrationHealth,
  InvitationFailure,
  NullableInstitutionType,
  OnboardingTask,
  PlatformAlert,
  PlatformDashboard,
  PrivilegedChange,
  ProductActivationSummary,
  ServiceHealth,
} from '@/types/platform';
import { formatCount, formatDateTime, formatRelative } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * PA-01, arranged as an answer to four questions rather than a tile wall.
 *
 * The test this screen has to pass is that an administrator opening it knows,
 * without scrolling or clicking, which institutions need attention, which
 * integrations are unhealthy, which product activations are stuck, and which
 * privileged actions just happened. So the layout is ordered by who is blocked:
 *
 *   1. a triage strip of the four counts, each one a jump link to its section;
 *   2. the work itself — institutions, alerts, integrations, activations,
 *      onboarding tasks, invitation failures;
 *   3. the audit trail, which is read after something has gone wrong;
 *   4. portfolio totals and the split by type, which are reference figures and
 *      are the least urgent thing on the page despite being the easiest to draw.
 *
 * Every section of the payload is optional, and each panel says so on its own
 * when its section is absent. That is what lets this ship before the backing
 * services are finished instead of after.
 */

function isPresent<T>(section: T[] | undefined): section is T[] {
  return Array.isArray(section);
}

export function PlatformDashboardPage() {
  const { t, i18n } = useTranslation();
  const typeLabel = useInstitutionTypeLabel();
  const locale = i18n.language;

  const fetcher = useCallback(() => platformApi.dashboard(), []);
  const { state, reload } = useAsyncData<PlatformDashboard>('platform-dashboard', fetcher);

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('platform.dashboard.title')}</h1>
          <p className={styles.pageLead}>{t('platform.dashboard.lead')}</p>
        </div>
        <button type="button" className={styles.secondaryBtn} onClick={reload}>
          <Icon name="refresh" size={15} />
          {t('platform.dashboard.refresh')}
        </button>
      </header>

      <AsyncPanel state={state} onRetry={reload}>
        {(data) => (
          <>
            <TriageStrip data={data} />

            <div className={styles.gridWide}>
              <Panel
                title={t('platform.dashboard.attention.title')}
                icon="warning"
                emphasis="attention"
                meta={countMeta(data.needsAttention, locale)}
              >
                <Section present={isPresent(data.needsAttention)} empty={data.needsAttention?.length === 0} emptyText={t('platform.dashboard.attention.empty')}>
                  <ul className={styles.rowList} id="pa-attention">
                    {data.needsAttention?.map((item) => (
                      <AttentionRow key={`${item.institutionId}-${item.reason}`} item={item} locale={locale} typeLabel={typeLabel} />
                    ))}
                  </ul>
                </Section>
              </Panel>

              <Panel title={t('platform.dashboard.alerts.title')} icon="notifications" meta={countMeta(data.alerts, locale)}>
                <Section present={isPresent(data.alerts)} empty={data.alerts?.length === 0} emptyText={t('platform.dashboard.alerts.empty')}>
                  <ul className={styles.rowList}>
                    {data.alerts?.map((alert) => (
                      <AlertRow key={alert.id} alert={alert} locale={locale} />
                    ))}
                  </ul>
                </Section>
              </Panel>
            </div>

            <div className={styles.gridTwo}>
              <Panel
                title={t('platform.dashboard.integrations.title')}
                icon="integration"
                emphasis="attention"
                meta={countMeta(data.integrations, locale)}
              >
                <Section present={isPresent(data.integrations)} empty={data.integrations?.length === 0} emptyText={t('platform.dashboard.integrations.empty')}>
                  <ul className={styles.rowList} id="pa-integrations">
                    {data.integrations?.map((integration) => (
                      <IntegrationRow key={integration.id} integration={integration} locale={locale} />
                    ))}
                  </ul>
                </Section>
              </Panel>

              <Panel
                title={t('platform.dashboard.activations.title')}
                icon="products"
                emphasis="attention"
                meta={countMeta(data.productActivations, locale)}
              >
                <Section present={isPresent(data.productActivations)} empty={data.productActivations?.length === 0} emptyText={t('platform.dashboard.activations.empty')}>
                  <ul className={styles.rowList} id="pa-activations">
                    {data.productActivations?.map((activation) => (
                      <ActivationRow
                        key={`${activation.institutionId}-${activation.productCode}`}
                        activation={activation}
                        locale={locale}
                      />
                    ))}
                  </ul>
                </Section>
              </Panel>
            </div>

            <div className={styles.gridTwo}>
              <Panel title={t('platform.dashboard.tasks.title')} icon="checklist" meta={countMeta(data.onboardingTasks, locale)}>
                <Section present={isPresent(data.onboardingTasks)} empty={data.onboardingTasks?.length === 0} emptyText={t('platform.dashboard.tasks.empty')}>
                  <ul className={styles.rowList}>
                    {data.onboardingTasks?.map((task) => (
                      <TaskRow key={`${task.caseId}-${task.label}`} task={task} locale={locale} />
                    ))}
                  </ul>
                </Section>
              </Panel>

              <Panel
                title={t('platform.dashboard.invitations.title')}
                icon="mail"
                meta={countMeta(data.invitationFailures, locale)}
              >
                <Section present={isPresent(data.invitationFailures)} empty={data.invitationFailures?.length === 0} emptyText={t('platform.dashboard.invitations.empty')}>
                  <ul className={styles.rowList} id="pa-invitations">
                    {data.invitationFailures?.map((failure) => (
                      <InvitationRow key={failure.invitationId} failure={failure} locale={locale} />
                    ))}
                  </ul>
                </Section>
              </Panel>
            </div>

            <div className={styles.gridWide}>
              <Panel title={t('platform.dashboard.privileged.title')} icon="history">
                <Section present={isPresent(data.privilegedChanges)} empty={data.privilegedChanges?.length === 0} emptyText={t('platform.dashboard.privileged.empty')}>
                  <ol className={styles.timeline} id="pa-privileged">
                    {data.privilegedChanges?.map((change) => (
                      <PrivilegedRow key={change.id} change={change} locale={locale} />
                    ))}
                  </ol>
                </Section>
              </Panel>

              <Panel title={t('platform.dashboard.services.title')} icon="pulse">
                <Section present={isPresent(data.services)} empty={data.services?.length === 0} emptyText={t('platform.dashboard.services.empty')}>
                  <ul className={styles.serviceList}>
                    {data.services?.map((service) => (
                      <ServiceRow key={service.name} service={service} locale={locale} />
                    ))}
                  </ul>
                </Section>
              </Panel>
            </div>

            <Panel
              title={t('platform.dashboard.portfolio.title')}
              icon="institution"
              actions={
                <Link className={styles.panelLink} to="/admin/institutions">
                  {t('platform.dashboard.portfolio.viewAll')}
                </Link>
              }
            >
              <Portfolio counts={data.counts} byType={data.byType} locale={locale} typeLabel={typeLabel} />
            </Panel>

            {data.generatedAt && (
              <p className={styles.generatedAt}>
                {t('platform.dashboard.generatedAt', { time: formatDateTime(data.generatedAt, locale) })}
              </p>
            )}
          </>
        )}
      </AsyncPanel>
    </div>
  );
}

/* ─── Triage ─── */

function TriageStrip({ data }: { data: PlatformDashboard }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const cards: ReadonlyArray<{ key: string; icon: IconName; anchor: string; value: number | undefined; tone: string }> = [
    {
      key: 'attention',
      icon: 'warning',
      anchor: '#pa-attention',
      value: data.needsAttention?.length,
      tone: styles.triageCritical,
    },
    {
      key: 'integrations',
      icon: 'integration',
      anchor: '#pa-integrations',
      value: data.integrations?.length,
      tone: styles.triageWarning,
    },
    {
      key: 'activations',
      icon: 'products',
      anchor: '#pa-activations',
      value: data.productActivations?.length,
      tone: styles.triageWarning,
    },
    {
      key: 'invitations',
      icon: 'mail',
      anchor: '#pa-invitations',
      value: data.invitationFailures?.length,
      tone: styles.triageInfo,
    },
  ];

  return (
    <div className={styles.triage}>
      {cards.map((card) => (
        <a key={card.key} href={card.anchor} className={`${styles.triageCard} ${card.tone}`}>
          <span className={styles.triageIcon} aria-hidden="true">
            <Icon name={card.icon} size={18} />
          </span>
          <span className={styles.triageValue}>
            {/* An absent section is not zero. Saying "0 unhealthy integrations"
                when the endpoint never answered is the one lie this screen must
                not tell. */}
            {card.value === undefined ? '—' : formatCount(card.value, locale)}
          </span>
          <span className={styles.triageLabel}>{t(`platform.dashboard.triage.${card.key}`)}</span>
        </a>
      ))}
    </div>
  );
}

/* ─── Section wrapper: absent vs empty are different facts ─── */

function Section({
  present,
  empty,
  emptyText,
  children,
}: {
  present: boolean;
  empty: boolean | undefined;
  emptyText: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();

  if (!present) {
    return (
      <p className={styles.sectionUnavailable} role="status">
        {t('platform.dashboard.sectionUnavailable')}
      </p>
    );
  }
  if (empty) {
    return (
      <p className={styles.sectionEmpty} role="status">
        <Icon name="check" size={15} aria-hidden="true" />
        {emptyText}
      </p>
    );
  }
  return <>{children}</>;
}

function countMeta(section: unknown[] | undefined, locale: string) {
  return section === undefined ? undefined : formatCount(section.length, locale);
}

/* ─── Rows ─── */

function AttentionRow({
  item,
  locale,
  typeLabel,
}: {
  item: AttentionItem;
  locale: string;
  typeLabel: (value: NullableInstitutionType | undefined) => string;
}) {
  const { t } = useTranslation();
  return (
    <li className={styles.row}>
      <div className={styles.rowMain}>
        <Link className={styles.rowTitleLink} to={`/admin/institutions/${encodeURIComponent(item.institutionId)}`}>
          {item.institutionName}
        </Link>
        <p className={styles.rowDetail}>{item.reason}</p>
        <p className={styles.rowMetaLine}>
          {typeLabel(item.institutionType)} · {item.institutionCode} ·{' '}
          {t('platform.dashboard.attention.since', { when: formatRelative(item.since, locale) })}
        </p>
      </div>
      <div className={styles.rowAside}>
        <StateBadge kind="severity" value={item.severity} size="sm" />
        <StateBadge kind="onboarding" value={item.onboardingState} size="sm" />
        {item.caseId && (
          <Link className={styles.rowAction} to={`/admin/onboarding/${encodeURIComponent(item.caseId)}`}>
            {t('platform.dashboard.attention.openCase')}
          </Link>
        )}
      </div>
    </li>
  );
}

function AlertRow({ alert, locale }: { alert: PlatformAlert; locale: string }) {
  return (
    <li className={styles.row}>
      <div className={styles.rowMain}>
        <p className={styles.rowTitle}>{alert.title}</p>
        {alert.detail && <p className={styles.rowDetail}>{alert.detail}</p>}
        <p className={styles.rowMetaLine}>
          {alert.institutionName ? `${alert.institutionName} · ` : ''}
          {formatRelative(alert.raisedAt, locale)}
        </p>
      </div>
      <div className={styles.rowAside}>
        <StateBadge kind="severity" value={alert.severity} size="sm" />
      </div>
    </li>
  );
}

function IntegrationRow({ integration, locale }: { integration: IntegrationHealth; locale: string }) {
  const { t } = useTranslation();
  return (
    <li className={styles.row}>
      <div className={styles.rowMain}>
        <p className={styles.rowTitle}>{integration.name}</p>
        {integration.institutionName && <p className={styles.rowMetaLine}>{integration.institutionName}</p>}
        {integration.lastError && <p className={styles.rowDetailError}>{integration.lastError}</p>}
        <p className={styles.rowMetaLine}>
          {t('platform.dashboard.integrations.checked', { when: formatRelative(integration.lastCheckedAt, locale) })}
          {typeof integration.consecutiveFailures === 'number' && integration.consecutiveFailures > 0
            ? ` · ${t('platform.dashboard.integrations.failures', { n: integration.consecutiveFailures })}`
            : ''}
        </p>
      </div>
      <div className={styles.rowAside}>
        <StateBadge kind="integration" value={integration.state} size="sm" />
      </div>
    </li>
  );
}

function ActivationRow({ activation, locale }: { activation: ProductActivationSummary; locale: string }) {
  const { t } = useTranslation();
  return (
    <li className={styles.row}>
      <div className={styles.rowMain}>
        <Link
          className={styles.rowTitleLink}
          to={`/admin/institutions/${encodeURIComponent(activation.institutionId)}#products`}
        >
          {activation.productName || activation.productCode}
        </Link>
        <p className={styles.rowMetaLine}>{activation.institutionName}</p>
        {activation.blockedOn && (
          <p className={styles.rowDetail}>{t('platform.dashboard.activations.blockedOn', { detail: activation.blockedOn })}</p>
        )}
        {activation.since && (
          <p className={styles.rowMetaLine}>{t('platform.dashboard.activations.since', { when: formatRelative(activation.since, locale) })}</p>
        )}
      </div>
      <div className={styles.rowAside}>
        <StateBadge kind="product" value={activation.state} size="sm" />
      </div>
    </li>
  );
}

function TaskRow({ task, locale }: { task: OnboardingTask; locale: string }) {
  const { t } = useTranslation();
  return (
    <li className={styles.row}>
      <div className={styles.rowMain}>
        <Link className={styles.rowTitleLink} to={`/admin/onboarding/${encodeURIComponent(task.caseId)}`}>
          {task.label}
        </Link>
        <p className={styles.rowMetaLine}>
          {task.institutionName}
          {task.assignedTo ? ` · ${task.assignedTo}` : ''}
        </p>
        {task.dueAt && (
          <p className={task.overdue ? styles.rowDetailError : styles.rowMetaLine}>
            {task.overdue
              ? t('platform.dashboard.tasks.overdue', { when: formatRelative(task.dueAt, locale) })
              : t('platform.dashboard.tasks.due', { when: formatRelative(task.dueAt, locale) })}
          </p>
        )}
      </div>
      <div className={styles.rowAside}>
        <StateBadge kind="onboarding" value={task.state} size="sm" />
      </div>
    </li>
  );
}

function InvitationRow({ failure, locale }: { failure: InvitationFailure; locale: string }) {
  return (
    <li className={styles.row}>
      <div className={styles.rowMain}>
        <p className={styles.rowTitle}>{failure.email}</p>
        <p className={styles.rowMetaLine}>
          {failure.institutionName} · {formatRelative(failure.failedAt, locale)}
        </p>
        {failure.reason && <p className={styles.rowDetailError}>{failure.reason}</p>}
      </div>
      <div className={styles.rowAside}>
        <StateBadge kind="invitation" value={failure.status} size="sm" />
        <Link className={styles.rowAction} to={`/admin/institutions/${encodeURIComponent(failure.institutionId)}#administrators`}>
          {/* Resending is done where the rest of that institution's invitations
              are, so the operator sees the whole picture before acting. */}
          <Icon name="chevronRight" size={14} />
        </Link>
      </div>
    </li>
  );
}

function PrivilegedRow({ change, locale }: { change: PrivilegedChange; locale: string }) {
  const { t } = useTranslation();
  return (
    <li className={styles.timelineItem}>
      <span className={`${styles.timelineDot} ${change.severity === 'CRITICAL' ? styles.timelineDotCritical : ''}`} aria-hidden="true" />
      <div className={styles.rowMain}>
        <p className={styles.rowTitle}>{change.action}</p>
        <p className={styles.rowMetaLine}>
          {t('platform.dashboard.privileged.by', { actor: change.actor })}
          {change.institutionName ? ` · ${change.institutionName}` : ''} · {formatDateTime(change.occurredAt, locale)}
        </p>
        {change.reason && <p className={styles.rowDetail}>{t('platform.dashboard.privileged.reason', { reason: change.reason })}</p>}
      </div>
    </li>
  );
}

function ServiceRow({ service, locale }: { service: ServiceHealth; locale: string }) {
  const { t } = useTranslation();
  return (
    <li className={styles.serviceRow}>
      <span className={styles.serviceName}>{service.name}</span>
      <StateBadge kind="integration" value={service.state} size="sm" />
      <span className={styles.serviceMeta}>
        {typeof service.latencyMs === 'number' ? t('platform.dashboard.services.latency', { ms: service.latencyMs }) : ''}
        {service.checkedAt ? ` · ${formatRelative(service.checkedAt, locale)}` : ''}
      </span>
    </li>
  );
}

/* ─── Portfolio (reference figures, deliberately last) ─── */

function Portfolio({
  counts,
  byType,
  locale,
  typeLabel,
}: {
  counts: PlatformDashboard['counts'];
  byType: InstitutionTypeCount[] | undefined;
  locale: string;
  typeLabel: (value: NullableInstitutionType | undefined) => string;
}) {
  const { t } = useTranslation();

  if (!counts && !byType) {
    return (
      <p className={styles.sectionUnavailable} role="status">
        {t('platform.dashboard.sectionUnavailable')}
      </p>
    );
  }

  const largest = byType?.reduce((max, entry) => Math.max(max, entry.count), 0) ?? 0;

  return (
    <div className={styles.portfolio}>
      {counts && (
        <dl className={styles.portfolioCounts}>
          {(
            [
              ['total', counts.total],
              ['active', counts.active],
              ['awaitingOnboarding', counts.awaitingOnboarding],
              ['suspended', counts.suspended],
            ] as const
          ).map(([key, value]) => (
            <div key={key} className={styles.portfolioCount}>
              <dt>{t(`platform.dashboard.portfolio.${key}`)}</dt>
              <dd>{formatCount(value, locale)}</dd>
            </div>
          ))}
        </dl>
      )}

      {byType && byType.length > 0 && (
        <ul className={styles.typeBars} aria-label={t('platform.dashboard.portfolio.byType')}>
          {byType.map((entry) => (
            <li key={entry.institutionType ?? 'RETIRED'} className={styles.typeBarRow}>
              <span className={styles.typeBarLabel}>{typeLabel(entry.institutionType)}</span>
              <span className={styles.typeBarTrack}>
                <span
                  className={styles.typeBarFill}
                  style={{ width: largest > 0 ? `${Math.max(2, (entry.count / largest) * 100)}%` : '2%' }}
                />
              </span>
              <span className={styles.typeBarValue}>{formatCount(entry.count, locale)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
